/* eslint-disable no-mixed-spaces-and-tabs */
import { formatCurrency, formatDateString } from '@/utils/constants';
import {
	ADDITIONAL_GOALS_OF_COVER,
	FULL_NEEDS_ANALYSIS,
	GOALS_OF_COVER_LABELS,
	NEEDS_ANALYSIS_LABELS,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';

export const EMAIL_TEMPLATES_COLLECTION = 'emailTemplates';
export const AI_HTML_TEMPLATES_COLLECTION = 'aiHtmlTemplates';
export const EMAIL_TEMPLATE_BLOCKS_COLLECTION = 'emailTemplateBlocks';

export const TEMPLATE_GROUPS = {
	master: 'Folio',
	user: 'My Templates',
	shared: 'Shared with Me',
	generated: 'Generated for Me',
};

export const FUSE_OPTIONS = {
	keys: ['name', 'subject'],
	threshold: 0.2,
};

export const checkClientScopeOfAdviceFullNeedsChecked = ({ scopeOfAdvice, classesOfRisk }) => {
	const fullNeedsChecked =
		scopeOfAdvice.includes(FULL_NEEDS_ANALYSIS) ||
		classesOfRisk
			.filter((v) => !v.isParent)
			.every((value) => scopeOfAdvice.includes(value.classOfRisk));
	return fullNeedsChecked;
};

/**
 * Includes both the standard goals of cover and any additional goals of cover in a single array of strings, with the standard goals of cover converted to their label values.
 * @param {Object} dealRow - The deal row object containing the goals of cover information.
 * @returns {Array} An array of strings representing the normalized goals of cover.
 */
const getNormalizedGoalsOfCover = (dealRow) => {
	const goalsOfCoverValues = Array.isArray(dealRow?.goalsOfCover)
		? dealRow.goalsOfCover.map((goal) => GOALS_OF_COVER_LABELS[goal] ?? goal).filter(Boolean)
		: [];
	const additionalGoalsOfCover = (dealRow?.[ADDITIONAL_GOALS_OF_COVER] ?? '').trim();

	return additionalGoalsOfCover
		? [...goalsOfCoverValues, additionalGoalsOfCover]
		: goalsOfCoverValues;
};

export const generateClientInfo = ({ client, dealRow }) => {
	const clientRow = client?.clientRow;
	const industry = clientRow?.industry;

	const { clientType, goalsOfCover, description: dealDescription } = dealRow ?? {};

	const clientTypeTitleCase = clientType
		? clientType?.replace(
				/\w\S*/g,
				(text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
			)
		: 'NONE';
	const normalizedGoalsOfCover = getNormalizedGoalsOfCover(dealRow);

	const { name: clientName } = clientRow ?? {};
	const description = clientRow?.description ?? dealDescription;

	const clientInfo = [
		{ name: 'Insured name', information: clientName },
		{ name: 'Client type', information: clientTypeTitleCase },
		...(industry ? [{ name: 'Industry', information: industry }] : []),
		...(description ? [{ name: 'Business description', information: description }] : []),
		...(clientRow?.numberOfEmployees
			? [{ name: 'No. of employees', information: clientRow.numberOfEmployees }]
			: []),
		...(clientRow?.annualRevenue
			? [
					{
						name: 'Annual turnover',
						information: formatCurrency({ num: clientRow.annualRevenue }),
					},
				]
			: []),
		...(normalizedGoalsOfCover.length > 0
			? [
					{
						name: 'Goals of cover',
						information: normalizedGoalsOfCover.join(', '),
					},
				]
			: []),
	];
	return clientInfo;
};

export const getDealHtmlValue = ({ dealRow, field, classesOfRisk }) => {
	const templateAttr = `template-value="${field.type}.${field.value}"`;

	if (dealRow?.[field.value] == null) return '';

	let dealValue =
		field.value === 'goalsOfCover'
			? [
					...(dealRow?.[field.value] ?? []).map((v) => GOALS_OF_COVER_LABELS[v] ?? ''),
					dealRow?.[ADDITIONAL_GOALS_OF_COVER] ?? '',
				].filter((v) => v.length > 0)
			: dealRow?.[field.value];

	// console.log('Deal value', dealValue, 'field value', field.value);

	if (field.value === 'riskClasses') {
		dealValue = dealValue
			.map((classOfRisk) => classesOfRisk.find((i) => i.classOfRisk === classOfRisk)?.label)
			.filter(Boolean)
			.join(',');
	}

	const value =
		typeof dealValue === 'string' || typeof dealValue === 'number'
			? field.label.toLowerCase().includes('date')
				? formatDateString(dealValue)
				: dealValue
			: dealValue && typeof dealValue === 'object' && Array.isArray(dealValue)
				? dealValue.join(', ')
				: dealValue && typeof dealValue === 'object'
					? Object.entries(dealValue)
							.map(([k, v]) => `${k}: ${v}`)
							.join(', ')
					: '';

	// console.log('getDealHtmlValue VALUE', { value, dealValue, typeof: typeof dealValue, field });
	if (field.node === 'ul' || field.node === 'ol') {
		if (dealValue && typeof dealValue === 'object' && Array.isArray(dealValue)) {
			let node = `<${field.node} ${templateAttr}>`;
			if (field.value === 'needsAnalysis') {
				const rows = dealValue
					.filter(
						(val) =>
							val.key &&
							classesOfRisk.find((i) => i.classOfRisk === val.key)?.label &&
							val.value &&
							NEEDS_ANALYSIS_LABELS[val.value],
					)
					.map(
						(val) =>
							`<li ${templateAttr}>${
								classesOfRisk.find((i) => i.classOfRisk === val.key)?.label
							}: ${NEEDS_ANALYSIS_LABELS[val.value]}</li>`,
					);
				node += rows.join('');
			} else {
				node += dealValue.map((val) => Object.entries(val).join(', '));
			}
			node += `</${field.node}>`;
			return node;
		}
	}
	return value;
};

export const formatDataForScopeOfAdvice = ({
	client,
	dealRow,
	userData,
	isMarble,
	classesOfRisk,
}) => {
	const clientRow = client?.clientRow;
	const industry = clientRow?.industry;

	const {
		clientType,
		goalsOfCover,
		needsAnalysis,
		description: dealDescription,
		// contactName,
		// clientName,
	} = dealRow ?? {};

	const { contactName, name: clientName } = clientRow ?? {};
	const description = clientRow?.description ?? dealDescription;

	const { name, position, phone, disclosureUrl, signature } = userData ?? {};
	const {
		fullLogo,
		name: brokerage,
		privacyStatement,
		termsOfEngagement,
		disclosureUrl: publicDisclosureUrl,
		colours,
		website,
		fspNumber,
		ledger,
	} = userData?.broker ?? {};

	const fullNeedsAnalysis = clientType
		? checkClientScopeOfAdviceFullNeedsChecked({
				scopeOfAdvice: dealRow?.riskClasses ?? [],
				classesOfRisk,
			})
		: false;

	const clientTypeTitleCase = clientType
		? clientType?.replace(
				/\w\S*/g,
				(text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
			)
		: 'NONE';

	const clientInfo = generateClientInfo({ client, dealRow });
	const normalizedGoalsOfCover = getNormalizedGoalsOfCover(dealRow);

	const fspValue = (fspNumber ?? '').toString().toLowerCase().includes('fsp')
		? fspNumber
		: `FSP ${fspNumber}`;
	const body = {
		brokerage: {
			...(userData?.broker ?? {}),
			name: brokerage,
			...((disclosureUrl ?? '').trim().length > 0 && { disclosureUrl }),
			...((publicDisclosureUrl ?? '').trim().length > 0 && { publicDisclosureUrl }),
			...((privacyStatement ?? '').trim().length > 0 && { privacyStatement }),
			...((termsOfEngagement ?? '').trim().length > 0 && { termsOfEngagement }),
			colours,
			website,
			...(fspNumber && { fspNumber: fspValue }),
			logoUrl: fullLogo,
			ledger: isMarble ? 'marble' : ledger,
		},
		clientInfo,
		contactName,
		clientName,
		clientType: clientTypeTitleCase,
		industry,
		description,
		numberOfEmployees: clientRow?.numberOfEmployees,
		annualTurnover: clientRow?.annualRevenue
			? formatCurrency({ num: clientRow.annualRevenue })
			: null,
		goalsOfCover: normalizedGoalsOfCover.join(', '),
		needsAnalysis: needsAnalysis
			? needsAnalysis.map((r) => ({
					key: classesOfRisk.find((item) => item.classOfRisk === r.key)?.label,
					value: NEEDS_ANALYSIS_LABELS[r.value],
				}))
			: null,
		fullNeedsAnalysis, //: (needsAnalysis ?? []).length === totalNeedsAnalysis,

		broker: {
			...(userData ?? {}),
			name,
			position,
			phone,
			...(signature && {
				signature: signature?.toString()?.replace(/>\s+</g, '><')?.replace(/\n/g, ''),
			}),
		},
	};

	return body;
};

export const extractBlockTypes = (html) => {
	if (!html) return [];
	const matches = [...html.matchAll(/{{{?\s*(\w+)\.(\w+)\s*}?}}/g)];
	const uniqueMatches = Array.from(new Set(matches.map((m) => `${m[1]}.${m[2]}`)));
	console.log('extractBlockTypes', uniqueMatches);
	return uniqueMatches;
};

export const safelyMergeObjects = (baseObject, newObject) => {
	return {
		...baseObject,
		...newObject,
		client: {
			...(baseObject?.client ?? {}),
			...(newObject?.client ?? {}),
		},
		deal: {
			...(baseObject?.deal ?? {}),
			...(newObject?.deal ?? {}),
		},
		broker: {
			...(baseObject?.broker ?? {}),
			...(baseObject?.user ?? {}),
			...(newObject?.broker ?? {}),
			...(newObject?.user ?? {}),
		},
		brokerage: {
			...(baseObject?.brokerage ?? {}),
			...(baseObject?.user?.broker ?? {}),
			...(newObject?.brokerage ?? {}),
			...(newObject?.user?.broker ?? {}),
		},
	};
};

